// Pure helpers behind the Amenity Identity Gap card's "What you want" column.
//
// Two shapes have shipped for `client_priorities`:
//   - Express query — `[{ amenity, gap: 'matched'|'missing', rank }, …]`
//   - Supabase RPC  — `["has_arcade", "has_bocce", …]`  (bare keys, alphabetical)
//
// The card read `p.amenity` / `p.gap` / `p.rank` off each entry, so under the RPC
// shape every row rendered a blank label, a bare "." where the rank goes, and an
// ✗ — because the matched-check fell back to set membership on an undefined
// amenity. Backend parity item #4.
//
// Two of the three fields are recoverable from the bare keys; `rank` is not.
// See `amenityPriorityRows` for why.

// Marker precedence for both columns of the card: ✓ first, ✗ second, ~ last.
// Kept here so the two columns cannot drift out of agreement.
const MARKER_ORDER = { aligned: 0, underrepresented: 1, unlisted: 2 };

/**
 * Has the client actually ranked any amenities?
 *
 * `fn_get_aim_amenity_gap` reports this directly as `has_client_priorities`.
 * Without it there is nothing to compare the AI's view against, and the card's
 * ✓/✗ verdicts would read as agreement or disagreement with a list that does
 * not exist. Falls back to the presence of `client_priorities` entries for the
 * Express payload, which carries no such flag.
 *
 * @param {object|null|undefined} payload the `fn_get_aim_amenity_gap` payload
 * @returns {boolean} true when the client has at least one prioritised amenity
 */
export function hasClientPriorities(payload) {
  if (typeof payload?.has_client_priorities === 'boolean') {
    return payload.has_client_priorities;
  }
  return Array.isArray(payload?.client_priorities) && payload.client_priorities.length > 0;
}

/**
 * The gap verdict for an AI-side row: `aligned` (✓), `underrepresented` (✗) or
 * `unlisted` (~ — the AI mentions it but it is not on the priority list).
 *
 * `fn_get_aim_amenity_gap` does not return `gap_status` (RPC-9 has not landed).
 * It does return `is_client_priority` and `client_rank`, which carry the same
 * information: a prioritised amenity the AI ranks at least as highly as the
 * client did is aligned; one the AI ranks lower is under-emphasised. Reading
 * only `gap_status` marked every row "not on your priority list" — including
 * the client's own priorities — for any client that had set some.
 *
 * `gap_status` is still preferred when present, so RPC-9 landing takes over
 * without a further change.
 *
 * @param {object|null|undefined} row a `rows[]` entry
 * @returns {'aligned'|'underrepresented'|'unlisted'}
 */
export function aiGapStatus(row) {
  const status = row?.gap_status;
  if (status === 'aligned' || status === 'underrepresented') return status;
  if (status) return 'unlisted';
  if (!row?.is_client_priority) return 'unlisted';
  // Number(null) is 0, which would read as "the client ranked it first" and
  // turn every unranked priority into a gap — check for absence first.
  const toRank = (v) => (v == null || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));
  const aiRank = toRank(row.ai_rank);
  const clientRank = toRank(row.client_rank);
  if (aiRank == null || clientRank == null) return 'aligned';
  return aiRank <= clientRank ? 'aligned' : 'underrepresented';
}

/**
 * Sort key for an AI-side row: ✓ first, ✗ second, ~ last.
 *
 * @param {object|null|undefined} row a `rows[]` entry
 * @returns {number} 0, 1 or 2
 */
export function aiMarkerOrder(row) {
  return MARKER_ORDER[aiGapStatus(row)];
}

/**
 * Reduce an amenity key or AI phrase to a comparable form.
 *
 * Strips the `has_` / `is_` / `num_` prefixes the priority keys carry, turns
 * underscores into spaces, and drops a trailing plural `s` so `has_hot_tubs`
 * lines up with the AI's `hot tub`.
 *
 * Matching stays exact after normalising rather than substring-based: "pool"
 * appears inside "heated pool", and treating that as a match would claim the AI
 * covers a priority it does not.
 *
 * @param {string|null|undefined} value raw key or phrase
 * @returns {string} normalised form, `''` for empty input
 */
export function normalizeAmenityKey(value) {
  if (value == null) return '';
  const stripped = String(value).trim().toLowerCase()
    .replace(/^(has_|is_|num_)/, '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return stripped.replace(/s$/, '');
}

/**
 * Build the "What you want" rows from whichever shape the endpoint returned.
 *
 * `ranked` tells the card whether it may number and rank-sort the list:
 *
 *   - `true`  — the payload carried real `rank` values (the query shape), so the
 *     numbering means something.
 *   - `false` — the RPC shape. `client_priorities` arrives **alphabetical**, not
 *     priority-ordered, so the array index is not a rank and presenting it as one
 *     would invent a priority order the client never set. Rows come back grouped
 *     by marker instead — ✓ before ✗ — matching the AI column's ordering.
 *
 * @param {Array|null|undefined} clientPriorities the payload's `client_priorities`
 * @param {Array|null|undefined} aiRows the payload's `rows`
 * @returns {{rows: Array<{amenity: string, matched: boolean, rank: number|null}>, ranked: boolean}}
 */
export function amenityPriorityRows(clientPriorities, aiRows) {
  const priorities = Array.isArray(clientPriorities) ? clientPriorities : [];
  const aiSet = new Set(
    (Array.isArray(aiRows) ? aiRows : []).map((r) => normalizeAmenityKey(r?.ai_amenity)),
  );
  aiSet.delete('');

  const entries = priorities
    .map((p) => {
      if (typeof p === 'string') return { amenity: p, gap: null, rank: null };
      if (p && typeof p === 'object') {
        return {
          amenity: p.amenity ?? '',
          gap: p.gap ?? null,
          rank: Number.isFinite(Number(p.rank)) ? Number(p.rank) : null,
        };
      }
      return null;
    })
    .filter((e) => e && e.amenity);

  const ranked = entries.some((e) => e.rank != null);

  const rows = entries.map((e) => ({
    amenity: e.amenity,
    rank: e.rank,
    // Prefer the backend's own verdict when it sent one; otherwise derive it.
    matched: e.gap ? e.gap === 'matched' : aiSet.has(normalizeAmenityKey(e.amenity)),
  }));

  if (ranked) {
    return { rows: rows.sort((a, b) => (a.rank ?? 99) - (b.rank ?? 99)), ranked };
  }
  // ✓ before ✗, then alphabetical within each group so the order is stable.
  return {
    rows: rows.sort((a, b) => (Number(b.matched) - Number(a.matched))
      || a.amenity.localeCompare(b.amenity)),
    ranked,
  };
}
